import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, ListItem, ListItemText, Typography } from '@mui/material';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import NavNeededIndicator from 'app/shared/componentsRestyled/common/NavNeededIndicator';
import { getRailAccentStyles } from 'app/shared/componentsRestyled/common/NavNeededIndicator/NavNeededIndicator.styles';
import {
  getAreaIdsByNameAndLevel,
  getNavNeededState,
} from 'app/shared/componentsRestyled/common/NavNeededIndicator/NavNeededIndicator.utils';
import { ListArrow } from 'assets/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { LoanApplicationTabName } from 'constants/loanApplication';
import { getConditionalObject } from 'utils/data';

import { setIsSidebarOpenAction } from 'store/global/actions';
import { selectIsSidebarInDrawer } from 'store/global/selectors';
import { clearEditStateAction } from 'store/loanApplications/actions';
import { selectEditingIds, selectIncompleteLinkedNav } from 'store/loanApplications/selectors';

import PresenceTabBadge from './components/PresenceTabBadge';
import SuppressedCountTabBadge from './components/SuppressedCountTabBadge';
import { ICON_SIZE, tabNameToAutoLockArea } from './LoanNavigationTabs.constants';
import {
  getIconStyles,
  getSubTabListStyles,
  getTabStyles,
  indicatorStyles,
  shortcutOverlayBadgeStyles,
} from './LoanNavigationTabs.styles';

const listItemTextSlotProps = {
  'data-truncated': true,
  className: 'truncate',
  component: 'p',
};

const LoanNavigationTab = ({
  tabKey,
  tabConfig,
  applicationId,
  subtabKey,
  isLastChild,
  tabNameToShortcut,
  shortcutPrefix,
  isModifierHeld,
}) => {
  const { state: navigationState, hash } = useLocation();

  const { tab, subtab: urlSubtab } = useParams();

  const dispatch = useDispatch();

  const editingIds = useSelector(selectEditingIds);
  const isSidebarInDrawer = useSelector(selectIsSidebarInDrawer);
  const navNeededByAreaId = useSelector(selectIncompleteLinkedNav());

  const isNewAlertsPageFT = useFeatureToggle(FeatureFlag.ShowNewAlertsPage);

  const onCloseSidebar = useCallback(() => dispatch(setIsSidebarOpenAction(false)), [dispatch]);

  const {
    subtabs,
    tabName,
    customName,
    Icon,
    alternativeIconNode,
    isExpanded,
    isAlwaysExpanded,
    tabRoute: nestedSubtabRoute,
    hasLink,
    isAutoLocked,
  } = tabConfig;

  const isMainLoanCategory = tabName === LoanApplicationTabName.LOANS;
  const isAlerts = tabName === LoanApplicationTabName.ALERTS;
  const hasSubtabs = !_.isEmpty(subtabs);
  const isDisabled = (isAlerts && !hasSubtabs) || isAutoLocked;
  const nestedSubtab = LoanApplicationTabName[subtabKey];
  const isAlertsSubtabs = nestedSubtab === LoanApplicationTabName.ALERTS;
  // TODO: get rid of isNestedSubtabsFlow when 1003 tab erased
  const isNestedSubtabsFlow = !!nestedSubtab;
  const parentSubtabKey = tabKey;
  const subtabRoute = isAlertsSubtabs
    ? nestedSubtabRoute
    : _.kebabCase(isNestedSubtabsFlow ? nestedSubtabRoute : tabName);
  const tabRoute = _.kebabCase(isNestedSubtabsFlow ? nestedSubtab : tabName);

  const isSomeTabBeingEdited = !!editingIds.length;
  const isLink = (!hasSubtabs || (hasLink && !isNewAlertsPageFT)) && !isAlerts && !isAutoLocked;

  const navNeededArea = tabNameToAutoLockArea[tabName];
  const { isNeeded: isNavNeeded } = getNavNeededState(
    navNeededByAreaId,
    navNeededArea ? getAreaIdsByNameAndLevel(navNeededArea.name, navNeededArea.levelType) : [],
  );

  const pathname = isNestedSubtabsFlow
    ? LoansPaths.applicationManagement.generatePath({
        [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
        [LOANS_ROUTING_PARAMS.tab]: tabRoute,
        [LOANS_ROUTING_PARAMS.subtab]: subtabRoute,
      })
    : LoansPaths.applicationManagement.generatePath({
        [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
        [LOANS_ROUTING_PARAMS.tab]: tabRoute,
      });

  const linkTo = isNestedSubtabsFlow
    ? {
        pathname,
        state: navigationState,
        ...getConditionalObject(!isNewAlertsPageFT, { search: `?subtab=${subtabRoute}` }),
        hash,
      }
    : pathname;

  const tabTitle = customName || tabName;
  const shortcutKey = tabNameToShortcut?.[tabTitle];
  const showShortcutOverlay =
    isModifierHeld && shortcutKey && shortcutPrefix && !hasSubtabs && !isAlerts;

  const isTabSelected = isNestedSubtabsFlow ? urlSubtab === subtabRoute : tab === tabRoute;

  const [isSubtabsExpanded, setIsSubtabsExpanded] = useState(
    isExpanded || isTabSelected || isAlwaysExpanded,
  );

  const toggleHandler = useCallback(() => {
    if (hasSubtabs) {
      setIsSubtabsExpanded(prev => !prev);
    }
  }, [hasSubtabs]);

  const onSubtabClickHandler = useCallback(() => {
    if (isSomeTabBeingEdited) {
      dispatch(clearEditStateAction());
    }
  }, [dispatch, isSomeTabBeingEdited]);

  const onTabClickHandler = useCallback(() => {
    if (isAlwaysExpanded || isAutoLocked) {
      return;
    }

    if (isNestedSubtabsFlow) {
      onSubtabClickHandler();
    }

    if (isSidebarInDrawer && isLink) {
      onCloseSidebar();
    }

    toggleHandler();
  }, [
    isAlwaysExpanded,
    isAutoLocked,
    isLink,
    isNestedSubtabsFlow,
    isSidebarInDrawer,
    onCloseSidebar,
    onSubtabClickHandler,
    toggleHandler,
  ]);

  return (
    <>
      <ListItem
        component={isLink ? Link : undefined}
        data-selected={isTabSelected}
        key={tabKey}
        sx={getTabStyles({
          isActiveSubTab: isTabSelected && !hasLink,
          isCategoryTab: hasSubtabs || isAlerts,
          isSubtabsExpanded: isSubtabsExpanded && !isAlerts,
          isClickable: !(isNewAlertsPageFT && isAlwaysExpanded),
          isDisabled,
        })}
        title={tabTitle}
        to={isLink ? linkTo : undefined}
        onClick={onTabClickHandler}
      >
        {isTabSelected && !hasSubtabs && !isAlerts && <Box sx={indicatorStyles} />}

        {isNavNeeded && !isTabSelected && <Box sx={getRailAccentStyles()} />}

        {Icon && (
          <Box
            sx={getIconStyles({
              isBackground: hasSubtabs || isAlerts,
              isDisabled,
            })}
          >
            <Icon size={ICON_SIZE} />
          </Box>
        )}

        {alternativeIconNode}

        <ListItemText primary={tabTitle} primaryTypographyProps={listItemTextSlotProps} />
        {isAlerts && !isNestedSubtabsFlow && <SuppressedCountTabBadge tab={tabName} />}
        {isNavNeeded && !isAlerts && <NavNeededIndicator />}
        {showShortcutOverlay && (
          <Typography sx={shortcutOverlayBadgeStyles} variant="bold12">
            {`${shortcutPrefix}${shortcutKey}`}
          </Typography>
        )}
        {!showShortcutOverlay && !hasSubtabs && !isAlerts && (
          <PresenceTabBadge
            subtabRoute={isAlertsSubtabs ? nestedSubtabRoute : null}
            tab={tabName}
          />
        )}
        {hasSubtabs && !isAlwaysExpanded && <ListArrow isExpanded={isSubtabsExpanded} />}
      </ListItem>
      {hasSubtabs && isSubtabsExpanded && (
        <Box sx={getSubTabListStyles({ isBorder: !isLastChild, isSubtabsExpanded })}>
          {Object.entries(subtabs).map(([tabKey, tabConfig], index) => {
            // TODO: delete isHidden and hashToInclude props after these subtabs will fully deleted
            if (tabConfig.isHidden && !hash.split('#').slice(1).includes(tabConfig.hashToInclude)) {
              return null;
            }

            return (
              <LoanNavigationTab
                applicationId={applicationId}
                isModifierHeld={isModifierHeld}
                key={index}
                shortcutPrefix={shortcutPrefix}
                subtabKey={!isMainLoanCategory ? parentSubtabKey : ''}
                tabConfig={tabConfig}
                tabKey={tabKey}
                tabNameToShortcut={tabNameToShortcut}
              />
            );
          })}
        </Box>
      )}
    </>
  );
};

LoanNavigationTab.propTypes = {
  tabKey: PropTypes.string.isRequired,
  tabConfig: PropTypes.object.isRequired,
  applicationId: PropTypes.string.isRequired,
  subtabKey: PropTypes.string,
  isLastChild: PropTypes.bool,
  isModifierHeld: PropTypes.bool,
  shortcutPrefix: PropTypes.string,
  tabNameToShortcut: PropTypes.objectOf(PropTypes.string),
};

export default memo(LoanNavigationTab);
